/**
 * ADL parsing (ADL 1.4 and ADL 2).
 */

export { ADL2Tokenizer, type Token, TokenType } from "./adl2_tokenizer.ts";
export { ADL2Parser, type ADL2ParseResult } from "./adl2_parser.ts";
export {
  parseAdl,
  type ParseAdlOptions,
  type ParseAdlResult,
} from "./parse_adl.ts";
export {
  type Adl14ConversionOptions,
  type Adl14ConversionResult,
  convertAdl14ToAdl2,
} from "./adl14_to_adl2_converter.ts";
export {
  type AdlDetectedVersion,
  detectAdlVersion,
  isAdl14,
} from "./adl_version.ts";
export {
  ArchetypeRepository,
  compileOetToOperational,
  detectTemplateInputFormat,
  getOperationalTemplateFromInput,
  isOetXml,
  isOptXml,
  isTemplateJson,
  parseOetXml,
  parseOptXml,
  parseTemplateInput,
  type ParseTemplateInputOptions,
  type ParseTemplateInputResult,
  parseTemplateJson,
  type TemplateInputFormat,
  type TemplateJsonParseResult,
} from "./legacy/mod.ts";
export {
  ARCHETYPE_VALUE_SETS_KEY,
  type Opt14Header,
  type Opt14UpconversionOptions,
  type Opt14UpconversionResult,
  type Opt14ValueSet,
  readOpt14Header,
  upconvertOpt14ToAdl2,
} from "./legacy/opt14_upconvert.ts";
export {
  type ArchetypeConversionLog,
  type ConversionCodeReason,
  type ConversionCreatedCode,
  type ConversionRunLog,
  type ConversionValueSet,
  parseConversionRunLog,
  serializeConversionRunLog,
} from "./legacy/conversion_log.ts";
export {
  restoreSyntheticNodeIds,
  SYNTHETIC_NODE_ID_ANNOTATION,
  withSyntheticNodeIdAnnotations,
} from "./synthetic_node_annotations.ts";
export {
  adl2CodingSystemOf,
  adl2CodingVersionWarning,
  adl2VersionFor,
  AT_CODED_ADL_VERSION,
  type CodedArtefact,
  isAdl14Artefact,
  isSyntheticNodeId,
  markSyntheticNodeId,
  SYNTHETIC_NODE_ID_KEY,
} from "./legacy/adl2_coding_system.ts";
export {
  type Adl2CodingOptions,
  type Adl2CodingSystem,
  collectAdl14NodeAndValueCodes,
  DEFAULT_ADL2_CODING_SYSTEM,
  isAdl14AtCode,
  Opt14CodeMinter,
  OPT14_CODE_MAPPING_VERSION,
  opt14CodeMapper,
  opt14CodeMappingVersion,
  opt14NodeIdToAdl2,
  opt14PathToAdl2,
  opt14TerminologyKeyToAdl2,
  opt14ValueCodeToAdl2,
  opt14ValueSetCodeToAdl2,
  resolveAdl2CodingSystem,
} from "./legacy/opt14_code_mapping.ts";
export { RulesParser } from "./rules_parser.ts";
export { serializeRulesSection } from "./rules_serializer.ts";
export {
  canBeGenerationRoot,
  getOperationalTemplateFromWorkspace,
  type ResolveOperationalOptions,
  type ResolveOperationalResult,
  resolveToOperationalTemplate,
  TemplateWorkspace,
  type TemplateWorkspaceFile,
} from "./template_workspace.ts";
export {
  type ClinicalModelExportEntry,
  type ClinicalModelFile,
  ClinicalModelWorkspace,
} from "./clinical_model_workspace.ts";
export {
  type GitHubFileEntry,
  type GitHubRepoRef,
  type GitHubTreeLoadResult,
  loadGitHubRepoTree,
  parseGitHubRepoSpec,
} from "./github_repo_loader.ts";
export {
  buildClinicalModelPathIndex,
  type ClinicalModelPathIndex,
  type GitHubFileRef,
  type GitHubTemplateClosureOptions,
  type GitHubTemplateClosureResult,
  type GitHubTemplateLoadProgress,
  loadGitHubClinicalModelClosure,
  loadGitHubTemplateClosure,
  parseGitHubClinicalModelFileUrl,
  parseGitHubTemplateFileUrl,
  resolveClinicalModelRef,
} from "./github_template_closure.ts";
export {
  type AnnotatedResource,
  type AnnotationDocumentation,
  annotationPathOf,
  buildDefinitionTree,
  type BuildDefinitionTreeOptions,
  countAnnotationKeysAtPath,
  type DefinitionTreeNode,
  ensureResourceAnnotations,
  getPathAnnotations,
  getResourceDocumentation,
  joinConstraintPath,
  listAnnotatedPaths,
  pathHasAnnotations,
  removeAllPathAnnotations,
  removePathAnnotation,
  resolveAnnotatedResource,
  serializeAnnotatedResource,
  setPathAnnotation,
} from "./clinical_model_annotations.ts";
export {
  type ApplyL10nResult,
  applyL10nWrites,
  generateL10nAnnotations,
  type GenerateL10nOptions,
  type L10nSourceNode,
  type L10nWrite,
  type L10nWriteKind,
  proposeL10nWrites,
} from "./l10n_annotation_generate.ts";
export {
  annotationFamily,
  type AnnotationPill,
  documentationViewForTree,
  familyFillColor,
  familyLegendLabel,
  flattenDefinitionTree,
  isLanguageIndependentFamily,
  KNOWN_FAMILIES,
  L10N_FAMILY,
  l10nSourcesFromTree,
  LANGUAGE_INDEPENDENT_FAMILIES,
  languageCode,
  languageOutlineColor,
  listFamilies,
  listLanguageBags,
  listResourceLanguages,
  mergeDocumentation,
  normalizeFamilyPrefix,
  orderLanguagesWithOriginal,
  originalLanguageOf,
  pillsAtPath,
  qualifyKeyForFamily,
  UNPREFIXED_FAMILY,
} from "./annotation_families.ts";
export {
  type AnnotationCopyItem,
  applyCopyItems,
  applyCopyItemsToDocumentation,
  copyItemId,
  defaultCopySelection,
  groupCopyItemsByFamily,
  listCopyItemsFromDocumentation,
} from "./copy_original_annotations.ts";
export {
  collectTemplateJsonExternalRefs,
  collectTemplateJsonExternalRefsFromText,
  collectTemplateJsonOverlayIds,
} from "./template_json_dependencies.ts";
export {
  CLINICAL_MODEL_EXTENSIONS,
  isClinicalModelPath,
  normalizeClinicalModelPath,
} from "./clinical_model_paths.ts";

export {
  documentationToBetterAnnotations,
  listTemplateJsonArchetypeIds,
  patchTemplateJsonAnnotations,
} from "./template_json_annotations.ts";
export {
  commitGitHubFile,
  decodeUtf8Base64,
  encodeUtf8Base64,
  getGitHubAuthenticatedUser,
  getGitHubFileContents,
  type GitHubCommitFileInput,
  type GitHubCommitFileResult,
  type GitHubContentsFile,
} from "./github_contents.ts";
export type { ClinicalModelGitHubSource } from "./clinical_model_workspace.ts";
